import React, { useState, useCallback } from 'react';
import { Modal, Tooltip, Spin, Button } from 'antd';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import styles from './RestoreModal.module.scss';

type Props = {
  visible: boolean;
  turbinePath: string;
  onRequest: (restoreDays: number) => void | Promise<void>;
  onCancel: () => void;
};

const MIN_DAYS = 1;
const MAX_DAYS = 180;

export const RestoreModal: React.FC<Props> = ({ visible, turbinePath, onRequest, onCancel }) => {
  const [value, setValue] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [touched, setTouched] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validate = useCallback((input: string): string => {
    if (!input.trim()) {
      return 'Restore days is required.';
    }

    if (!/^\d+$/.test(input.trim())) {
      return 'Please enter a valid whole number.';
    }

    const num = parseInt(input.trim(), 10);

    if (num < MIN_DAYS) {
      return `Minimum restore duration is ${MIN_DAYS} day.`;
    }

    if (num > MAX_DAYS) {
      return `Maximum restore duration is ${MAX_DAYS} days.`;
    }

    return '';
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (raw !== '' && !/^\d*$/.test(raw)) {
      return;
    }

    setValue(raw);
    setDirty(true);

    if (touched) {
      setError(validate(raw));
    }
  };

  const handleBlur = () => {
    if (dirty) {
      setTouched(true);
      setError(validate(value));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
      e.preventDefault();
    }

    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setTouched(true);
    const validationError = validate(value);
    setError(validationError);

    if (!validationError) {
      const result = onRequest(parseInt(value.trim(), 10));
      if (result instanceof Promise) {
        setIsLoading(true);
        try {
          await result;
        } catch {
          setIsLoading(false);
        }
      }
    }
  };

  const handleAfterClose = () => {
    setValue('');
    setError('');
    setTouched(false);
    setDirty(false);
    setIsLoading(false);
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable={false}
      centered
      width={560}
      bodyStyle={{ padding: 0 }}
      destroyOnClose
      afterClose={handleAfterClose}
    >
      <Spin spinning={isLoading}>
        <div className={styles.container}>
          <div className={styles.headerSection}>
            <h2 className={styles.title}>Request Turbine Restore</h2>
            <CloseOutlined className={styles.closeIcon} onClick={onCancel} />
          </div>

          <div className={styles.body}>
            <div className={styles.infoSection}>
              <span className={styles.infoLabel}>Turbine path</span>
              <div className={styles.turbinePathValue}>{turbinePath}</div>
            </div>

            <div className={styles.fieldSection}>
              <div className={styles.fieldLabelRow}>
                <span className={styles.fieldLabel}>Restore days</span>
                <Tooltip
                  placement='right'
                  title={`Number of days the turbine data will be available after restoring from deep archive (${MIN_DAYS}–${MAX_DAYS}).`}
                >
                  <InfoCircleOutlined className={styles.infoIcon} />
                </Tooltip>
              </div>

              <input
                type='text'
                inputMode='numeric'
                className={`${styles.numberInput} ${error ? styles.numberInputError : ''}`}
                placeholder={`Enter number of days (${MIN_DAYS}–${MAX_DAYS})`}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                autoFocus
                disabled={isLoading}
              />

              {error && <span className={styles.errorMessage}>{error}</span>}
            </div>
          </div>

          <div className={styles.footerSection}>
            <Button onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type='primary' onClick={handleSubmit} disabled={isLoading}>
              Request
            </Button>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};
